import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";

import {
    ARCHIVED_ASSET_STATUS,
    ASSET_PREVIEW_STATUS,
    ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
    ASSET_TYPES,
    COMMON_STATUS,
    GROUPING_VIEW_TYPE,
    LIST_VIEW_TYPE,
    MEDIA_TYPES,
} from "../../constants";
import { MediaImage } from "./components";
import { useRoutePath } from "../../hooks/useRoutePath";
import { Link } from "react-router-dom";
import convertToByte from "../../cores/convertToByte";
import { AssetStatusArchived, AssetStatusArchiving, AssetStatusRestoring } from "./AssetStatus";
import { siteMode } from "../../cores/siteMode";
import compareVersions, {
    VERSION_CM_11376_SHARES_V2,
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_13925_OWNER_LOCK,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_14205_ASSET_STARRED,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
    VERSION_CM_8731_ASSET_DOWNLOAD_JOBS,
    VERSION_CM_8782_ASSET_SHARE_LINK,
} from "@cores/version";
import { getAsset } from "@modules/asset";
import menuService from "@services/menuService";
import { Box, Card, CardContent, CardHeader, Checkbox, Divider, Grid, Skeleton, Stack, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconAi, IconDrm, IconLink, IconLock, IconMoreHoriz, IconNew, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { alpha, CardActionArea } from "@mui/material";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import useCheckPermission from "@hooks/useCheckPermission";
import { Menu } from "@components_v2/menu";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";
import { CircledIcon } from "@components_v2/icon";

const AssetCard = ({
    asset,
    checkable,
    checked,
    draggable,
    thumbnailCrop,
    onDragStartItem = function () {},
    onDragEndItem = function () {},
    onChangeCheck = function () {},
    onClickDeleteAsset = function () {},
    onClickCreateJob = function () {},
    onClickRow = function () {},
    onDoubleClickRow = function () {},
    renderTools,
    hiddenCreateJobButton,
    onToggleModal = function () {},
    onClickName,
    onClickDownload = function () {},
    onClickStarred = function () {},
    onClickOwnerLock = function () {},
}) => {
    const { propertyPanelTargetId, stage } = useSelector(({ stage, propertyPanel }) => ({
        propertyPanelTargetId: propertyPanel.targetId,
        stage,
    }));
    const routePath = useRoutePath();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const isLibrary = siteMode.get() === "CMS";

    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const VIDEOS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Videos" });
    const PHOTO_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Photo" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const [isShowOptions, setIsShowOptions] = useState(false);
    const [isShowImagePreviewModal, setIsShowImagePreviewModal] = useState(false);
    const [isHover, setIsHover] = useState(false);

    const isActive = asset.id === propertyPanelTargetId;
    const isInactive = asset.status === COMMON_STATUS.INACTIVE;
    const isCms = siteMode.get() === "CMS";
    const disabledCreateJobButton = (asset) => {
        if (
            asset.status === COMMON_STATUS.INACTIVE ||
            asset.mediaType !== MEDIA_TYPES.VIDEO ||
            asset.type === ASSET_TYPES.DASH ||
            asset.type === ASSET_TYPES.HLS
        )
            return true;

        return false;
    };

    const disabledCreateJob = disabledCreateJobButton(asset);

    const [isDragging, setIsDragging] = useState(false);

    const { assets } = useSelector(({ assets }) => ({ assets }));

    const onClickCard = (assetId, projectId) => {
        onClickRow(assetId, projectId);
    };
    const onDoubleClickCard = (assetId, projectId) => {
        if (isShowImagePreviewModal) {
            return;
        }

        onDoubleClickRow(asset);
    };

    const handleShowImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(true);
    }, [isShowImagePreviewModal]);

    const handleCloseImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(false);
    }, [isShowImagePreviewModal]);

    const handleClickImageAssetToPhoto = useCallback(async () => {
        const response = await dispatch(getAsset(asset.id));
        if (!response?.data) {
            return;
        }

        const photoAsset = response.data;
        history.push(`${menuService.paths.contentsPhotoCreate(spaceId, projectId)}`, {
            photoAsset,
        });
    }, [asset, history]);

    const getSkeleton = ({ type, width, height }) => (
        <Skeleton
            variant={type}
            animation={"wave"}
            width={width}
            height={height}
            sx={{ backgroundColor: alpha("#dfdfee", 0.5) }}
        />
    );

    const assetDetailPath = useMemo(() => {
        if (onClickName) return onClickName(asset);
        else
            return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                asset.project?.id
            }`;
    }, [spaceId, projectId, asset]);

    const hasDownloadPermissionByShare = useMemo(() => {
        const isExpired = asset?.shareInfo?.expiresAt
            ? countDaysBetweenDates({ toDate: dayjs(asset?.shareInfo?.expiresAt) }) < 0
            : false;

        const isExceedDownloadCount =
            asset?.shareInfo?.maxDownloads > 0 && asset?.shareInfo?.maxDownloads <= asset?.shareInfo?.downloadCount;

        return asset?.shareInfo && !(isExpired || isExceedDownloadCount);
    }, [asset]);

    const isOwner = useMemo(() => {
        return (
            (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                ? asset.createdBy?.id
                : asset.owner?.id) === stage.user?.id
        );
    }, [stage, asset]);

    const disabledByOwnerLock = useMemo(() => {
        return asset?.isOwnerLocked && !isOwner;
    }, [asset, isOwner]);

    const hiddenDownloadButton = useMemo(() => {
        return (
            (!asset?.shareInfo || !hasDownloadPermissionByShare) &&
            (!DOWNLOAD_PERMISSIONS.DOWNLOAD || disabledByOwnerLock)
        );
    }, [asset, hasDownloadPermissionByShare, DOWNLOAD_PERMISSIONS, disabledByOwnerLock]);

    return (
        <Card
            sx={{
                position: `relative`,
                width: `202px`,
                boxShadow: `none`,
                ".btn-dropdown": {
                    opacity: 0,
                    top: 10,
                    right: 10,
                    ".MuiIconButton-root": {
                        backgroundColor: alpha(theme.palette.constant.black, 0.6),
                    },
                    "&.dropdown-open": {
                        opacity: 1,
                        ".MuiIconButton-root": {
                            backgroundColor: alpha(theme.palette.constant.black, 0.92),
                        },
                    },
                },
                "&:hover .btn-dropdown": {
                    opacity: 1,
                    ".MuiIconButton-root:hover": {
                        backgroundColor: alpha(theme.palette.constant.black, 0.8),
                    },
                },
            }}
            draggable={draggable}
            onDragStart={(e) => {
                e.stopPropagation();
                setIsDragging(true);
                if (onDragStartItem) onDragStartItem(e, asset);
            }}
            onDragEnd={(e) => {
                e.stopPropagation();
                setIsDragging(false);
                if (onDragEndItem) onDragEndItem(e, asset);
            }}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => {
                setIsHover(false);
                setIsShowOptions(false);
            }}
        >
            <CardHeader
                action={
                    <>
                        <Grid sx={{ mb: theme.spacing(1) }}>
                            {checkable ? (
                                <Checkbox
                                    color={"greySecondary"}
                                    checked={checked}
                                    disabled={asset.state === "DRAFT"}
                                    onChange={() => {
                                        if (onChangeCheck) onChangeCheck(asset);
                                    }}
                                />
                            ) : (
                                <div></div>
                            )}
                        </Grid>
                        {isHover && (
                            <Menu
                                menuMinWidth={160}
                                placement={"right"}
                                type={"icon"}
                                label={<IconMoreHoriz size={16} />}
                                size={"small"}
                                list={[
                                    // {
                                    //     skipTranslationLabel: t(`common::label::Share`, "Share"),
                                    //     onClick: (e) => {
                                    //         e.preventDefault();
                                    //         onToggleModal("share", true, asset);
                                    //     },
                                    //     separate: true,
                                    //     buttonDisabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNAL,
                                    //     enable:
                                    //         compareVersions(stage.version, VERSION_CM_8782_ASSET_SHARE_LINK) >= 0 && !isCms,
                                    // },
                                    {
                                        label: t(`common::label::Share link`, `Share link`),
                                        onClick: () => {
                                            onToggleModal("shareLink", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            !SHARES_PERMISSIONS.EXTERNAL ||
                                            disabledByOwnerLock,
                                        hidden: !(
                                            !isCms &&
                                            compareVersions(stage.version, VERSION_CM_8782_ASSET_SHARE_LINK) >= 0
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Share request`, `Share request`),
                                        onClick: () => {
                                            onToggleModal("requestShare", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden: !(
                                            !isCms &&
                                            compareVersions(stage.version, VERSION_CM_11376_SHARES_V2) >= 0 &&
                                            compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) < 0
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Copy to`, `Copy to`),
                                        divider: true,
                                        onClick: () => {
                                            onToggleModal("copyCategory", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: !(
                                            !isCms &&
                                            assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                                            assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Move`, `Move`),
                                        divider: !(
                                            assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                                            assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                                        ),
                                        onClick: (e) => {
                                            if (assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                                                onToggleModal("moveCategory", true, asset);
                                            } else {
                                                onToggleModal("moveFolder", true, asset);
                                            }
                                            //category, folder에 따라 분기처리가 필요
                                        },
                                        disabled:
                                            asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: !(
                                            !isCms && assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Rename`, `Rename`),
                                        divider: assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST,
                                        onClick: () => {
                                            onToggleModal("rename", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: isCms,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Download`, `Download`),
                                        divider: !isCms,
                                        onClick: () => {
                                            onClickDownload(asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            (compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 &&
                                                !DOWNLOAD_PERMISSIONS.DOWNLOAD),
                                        hidden:
                                            compareVersions(stage.version, VERSION_CM_8731_ASSET_DOWNLOAD_JOBS) < 0 ||
                                            (compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 &&
                                                hiddenDownloadButton),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Request download`, `Request download`),
                                        onClick: () => {
                                            onToggleModal("requestDownload", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden:
                                            compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 ||
                                            !hiddenDownloadButton,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Request`, `Request`),
                                        onClick: () => {
                                            onToggleModal("request", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(
                                            `common::label::${asset?.myFavorite ? "Unstarred" : "Starred"}`,
                                            asset?.myFavorite ? "Unstarred" : "Starred",
                                        ),
                                        onClick: () => {
                                            onClickStarred([asset.id], asset.myFavorite);
                                        },
                                        disabled: asset.state === "DRAFT",
                                        hidden: compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0,
                                        clickClose: true,
                                    },
                                    {
                                        label: asset.isOwnerLocked
                                            ? t(`common::label::Unlock`, `Unlock`)
                                            : t(`common::label::Lock`, `Lock`),
                                        divider: false,
                                        onClick: () => {
                                            onClickOwnerLock(asset);
                                        },
                                        hidden: !(
                                            !isCms && compareVersions(stage.version, VERSION_CM_13925_OWNER_LOCK) >= 0
                                        ),
                                        disabled: asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || !isOwner,
                                        clickClose: true,
                                    },
                                    // {
                                    //     label: t(`common::label::Archive`, `Archive`),
                                    //     separate: true,
                                    //     onClick: (e) => {

                                    //     },
                                    //     enable: !isCms,
                                    // },
                                    {
                                        label: t(`common::label::Delete`, `Delete`),
                                        divider: true,
                                        onClick: () => {
                                            if (onClickDeleteAsset) onClickDeleteAsset(asset);
                                        },
                                        disabled: !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: isCms,
                                        clickClose: true,
                                    },
                                ]}
                                useInCard={true}
                                sx={{
                                    mt: theme.spacing(1.5),
                                    mr: theme.spacing(1.5),
                                }}
                            />
                        )}
                    </>
                }
                sx={{
                    position: `absolute`,
                    width: `100%`,
                    zIndex: `1`,
                    p: 0,
                    ".MuiCardHeader-content": {
                        display: `none`,
                    },
                    ".MuiCardHeader-action": {
                        flexGrow: 1,
                        display: `flex`,
                        justifyContent: `space-between`,
                        m: 0,
                    },
                }}
            />
            <CardActionArea
                onClick={(e) => {
                    console.log(e);
                    e.stopPropagation();
                    onClickCard(asset.id, asset.project.id);
                    // if (onClickCardCover) onClickCardCover(asset.id);
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (ASSETS_PERMISSIONS.READ) onDoubleClickCard(asset.id, asset.project.id);
                    // if (onDoubleClickCardCover) onDoubleClickCardCover(asset.id);
                }}
                sx={{ cursor: isLibrary ? "default" : "pointer" }}
            >
                <MediaImageWrapper
                    status={
                        asset.state === "DRAFT"
                            ? "IN_PROGRESS"
                            : asset.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR)?.[0]?.status
                    }
                    sx={{
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        width: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
                        height: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
                        backgroundColor: isActive
                            ? alpha(theme.palette.greySecondary.A100, 0.6)
                            : checked
                            ? alpha(theme.palette.greySecondary.A100, 0.4)
                            : alpha(theme.palette.greySecondary[50], 0.5),
                        borderRadius: `8px`,
                    }}
                >
                    <>
                        <MediaImage
                            variant={"card"}
                            draggable={draggable}
                            asset={asset}
                            size={"large"}
                            thumbnailCrop={thumbnailCrop}
                            errorIconProps={{ position: "right", size: 16 }}
                            isShowImagePreviewModal={isShowImagePreviewModal}
                            handleShowImagePreviewModal={handleShowImagePreviewModal}
                            handleCloseImagePreviewModal={handleCloseImagePreviewModal}
                        />
                        {asset.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVED && (
                            <AssetStatusArchived containerType={"archive"} status={asset.archiveStatus} />
                        )}
                        {asset.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVING && (
                            <AssetStatusArchiving containerType={"archive"} />
                        )}
                        {asset.archiveStatus === ARCHIVED_ASSET_STATUS.RESTORING && (
                            <AssetStatusRestoring containerType={"archive"} />
                        )}
                        {countDaysBetweentDates({ fromDate: asset.createdAt }) < 7 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    right: theme.spacing(1),
                                    bottom: theme.spacing(1),
                                    color: theme.palette.error.main,
                                }}
                            >
                                <IconNew size={34} viewBox={"0 0 34 16"} style={{ height: "16px" }} />
                            </Box>
                        )}
                    </>
                </MediaImageWrapper>
            </CardActionArea>

            <CardContent sx={{ px: 1, "&:last-of-type": { pb: 2 } }}>
                <Stack>
                    <Grid container alignItems={"center"} flexWrap={"nowrap"} sx={{ overflow: `hidden` }}>
                        <AssetTypeFlag minWidth={22} width={22} height={16} type={asset.mediaType} />
                        <Grid item xs sx={{ textOverflow: `ellipsis`, overflow: `hidden` }}>
                            <Typography variant={"body1_500"} title={asset.name} noWrap>
                                <Link
                                    to={assetDetailPath}
                                    style={{ color: `inherit`, ":hover": { textDecoration: `none` } }}
                                    disabled={!ASSETS_PERMISSIONS.READ}
                                >
                                    {asset.name}
                                </Link>
                            </Typography>
                        </Grid>
                        <Stack direction={"row"} gap={theme.spacing(0.5)}>
                            {asset.isConnected ? <IconLink size={16} /> : ""}
                            {asset.myFavorite ? <IconStar size={16} htmlColor={theme.palette.etc.orange500} /> : ""}
                        </Stack>
                    </Grid>

                    <Grid container alignItems={"center"} gap={0.5} mt={1} flexWrap={"nowrap"}>
                        {asset.state === "DRAFT" ? (
                            <>
                                <Grid item xs>
                                    <Grid container flexDirection={"row"}>
                                        {getSkeleton({ type: "rounded", width: 40, height: 18 })}
                                        <Divider flexItem orientation={"vertical"} sx={{ my: 0.5, mx: 1 }} />
                                        {getSkeleton({ type: "rounded", width: 60, height: 18 })}
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs>
                                    <Grid container>
                                        <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                            {asset.extensions ? asset.extensions : "-"}
                                        </Typography>
                                        <Divider
                                            flexItem
                                            orientation={"vertical"}
                                            sx={{ my: 0.5, mx: theme.spacing(0.75) }}
                                        />
                                        <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                            {asset.size ? convertToByte(asset.size, true) : "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Stack direction={"row"} gap={theme.spacing(0.5)} sx={{ "& i.sprite + *": { ml: 0 } }}>
                                    {asset.hasDubbing && (
                                        <CircledIcon size={20} color={theme.palette.etc.purple400}>
                                            <IconVoice size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.encryptions?.indexOf("DRM") > -1 && (
                                        <CircledIcon size={20} color={theme.palette.warning.main}>
                                            <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.isAnalyzed && (
                                        <CircledIcon size={20} color={theme.palette.success.light}>
                                            <IconAi size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.isOwnerLocked && (
                                        <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                            <IconLock size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                </Stack>
                            </>
                        )}
                    </Grid>
                </Stack>
            </CardContent>

            {/*{checkable && (*/}
            {/*    <label htmlFor={asset.id} className="form-checkbox">*/}
            {/*        <input*/}
            {/*            id={asset.id}*/}
            {/*            type="checkbox"*/}
            {/*            checked={checked}*/}
            {/*            onChange={() => {*/}
            {/*                if (onChangeCheck) onChangeCheck(asset);*/}
            {/*            }}*/}
            {/*        />*/}
            {/*        <i></i>*/}
            {/*    </label>*/}
            {/*)}*/}
            {/*<CardCover>*/}
            {/*    <button*/}
            {/*        type="button"*/}
            {/*        onClick={(e) => {*/}
            {/*            e.stopPropagation();*/}
            {/*            onClickCard(asset.id, asset.project.id);*/}
            {/*            // if (onClickCardCover) onClickCardCover(asset.id);*/}
            {/*        }}*/}
            {/*        onDoubleClick={(e) => {*/}
            {/*            e.stopPropagation();*/}
            {/*            onDoubleClickCard(asset.id, asset.project.id);*/}
            {/*            // if (onDoubleClickCardCover) onDoubleClickCardCover(asset.id);*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        <MediaImage*/}
            {/*            draggable={draggable}*/}
            {/*            asset={asset}*/}
            {/*            isShowImagePreviewModal={isShowImagePreviewModal}*/}
            {/*            handleShowImagePreviewModal={handleShowImagePreviewModal}*/}
            {/*            handleCloseImagePreviewModal={handleCloseImagePreviewModal}*/}
            {/*        />*/}
            {/*    </button>*/}
            {/*</CardCover>*/}
            {/*{asset.status === "Archived" && <AssetStatusArchived />}*/}
            {/*{asset.status === "Archiving" && <AssetStatusArchiving />}*/}
            {/*{asset.status === "Restoring" && <AssetStatusRestoring />}*/}

            {/*<CardBody>*/}
            {/*    <div className={"type"}>*/}
            {/*        <FormatBadge format={asset.type} label={asset.type} mediaType={asset.mediaType} />*/}
            {/*        {asset.isConnected ? <i className="sprite sprite-reference-linked"></i> : ""}*/}
            {/*    </div>*/}
            {/*    <Link*/}
            {/*        to={`${routePath.location.pathname}/${asset.id}?projectId=${asset.project.id}`}*/}
            {/*        className={"text-default ellipsis-flex ellipsis-flex-inline mt-2"}*/}
            {/*    >*/}
            {/*        <p className={"ellipsis"} title={asset.name}>*/}
            {/*            {asset.name}*/}
            {/*        </p>*/}
            {/*        {asset.hasDubbing && <i className={"sprite sprite-dubbing"}></i>}*/}
            {/*        {asset.encryptions && asset.encryptions.indexOf("DRM") > -1 && (*/}
            {/*            <i className="nowrap sprite sprite-drm"></i>*/}
            {/*        )}*/}
            {/*        {asset.isAnalyzed && <i className={"sprite sprite-ai-video"}></i>}*/}
            {/*    </Link>*/}
            {/*    <small>{asset.id}</small>*/}
            {/*    <div className={"additional"}>*/}
            {/*        <p>{asset.size ? convertToByte(asset.size) : "-"}</p>*/}
            {/*    </div>*/}
            {/*</CardBody>*/}
            {/*<CardTool>*/}
            {/*    <PopperMenu*/}
            {/*        buttonComponentDisabled={hiddenCreateJobButton && isCms}*/}
            {/*        ButtonComponent={*/}
            {/*            <button type="button" className="btn btn-xs btn-round btn-dropdown-trigger">*/}
            {/*                <i className="sprite sprite-etc"></i>*/}
            {/*                <span className={"hidden"}>{t(`common::label::Etc`)}</span>*/}
            {/*            </button>*/}
            {/*        }*/}
            {/*        menuButtons={[*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Create {{name}}`, `Create {{name}}`, {*/}
            {/*                    name: t(`common::label::Video`, `Video`),*/}
            {/*                }),*/}
            {/*                onClick: (e) => {*/}
            {/*                    if (onClickCreateJob) onClickCreateJob(asset.id);*/}
            {/*                },*/}
            {/*                buttonDisabled: disabledCreateJob,*/}
            {/*                enable: !hiddenCreateJobButton && asset.mediaType === MEDIA_TYPES.VIDEO,*/}
            {/*            },*/}

            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Create {{name}}`, `Create {{name}}`, {*/}
            {/*                    name: t(`common::label::Photo`, `Photo`),*/}
            {/*                }),*/}
            {/*                onClick: (e) => {*/}
            {/*                    e.stopPropagation();*/}

            {/*                    handleClickImageAssetToPhoto();*/}
            {/*                },*/}
            {/*                buttonDisabled: false,*/}
            {/*                enable: !hiddenCreateJobButton && asset.mediaType === MEDIA_TYPES.IMAGE,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Internal Share`, "Internal Share"),*/}
            {/*                onClick: (e) => {*/}
            {/*                    e.preventDefault();*/}
            {/*                    onToggleModal("share", true, asset);*/}
            {/*                },*/}
            {/*                separate: true,*/}
            {/*                enable: compareVersions(stage.version, VERSION_CM_8782_ASSET_SHARE_LINK) >= 0,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::External Share`, `External Share`),*/}
            {/*                onClick: (e) => {*/}
            {/*                    onToggleModal("externalShare", true, asset);*/}
            {/*                },*/}
            {/*                enable: !isCms && compareVersions(stage.version, VERSION_CM_8782_ASSET_SHARE_LINK) >= 0,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Copy to`, `Copy to`),*/}
            {/*                separate: true,*/}
            {/*                onClick: (e) => {*/}
            {/*                    onToggleModal("copyCategory", true, asset);*/}
            {/*                },*/}
            {/*                enable:*/}
            {/*                    !isCms &&*/}
            {/*                    assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&*/}
            {/*                    assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Move`, `Move`),*/}
            {/*                separate: !(*/}
            {/*                    assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&*/}
            {/*                    assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY*/}
            {/*                ),*/}
            {/*                onClick: (e) => {*/}
            {/*                    if (assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {*/}
            {/*                        onToggleModal("moveCategory", true, asset);*/}
            {/*                    } else {*/}
            {/*                        onToggleModal("moveFolder", true, asset);*/}
            {/*                    }*/}
            {/*                    //category, folder에 따라 분기처리가 필요*/}
            {/*                },*/}
            {/*                enable: !isCms && assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Rename`, `Rename`),*/}
            {/*                separate: assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST,*/}
            {/*                onClick: (e) => {*/}
            {/*                    onToggleModal("rename", true, asset);*/}
            {/*                },*/}
            {/*                enable: !isCms,*/}
            {/*            },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Download`, `Download`),*/}
            {/*                separate: true,*/}
            {/*                onClick: (e) => {*/}
            {/*                    handleClickDownload(asset);*/}
            {/*                },*/}
            {/*                enable: !isCms && compareVersions(stage.version, VERSION_CM_8731_ASSET_DOWNLOAD_JOBS) >= 0,*/}
            {/*            },*/}
            {/*            // {*/}
            {/*            //     skipTranslationLabel: t(`common::label::Archive`, `Archive`),*/}
            {/*            //     separate: true,*/}
            {/*            //     onClick: (e) => {*/}

            {/*            //     },*/}
            {/*            //     enable: !isCms,*/}
            {/*            // },*/}
            {/*            {*/}
            {/*                skipTranslationLabel: t(`common::label::Delete`, `Delete`),*/}
            {/*                separate: true,*/}
            {/*                onClick: (e) => {*/}
            {/*                    console.log("click delete");*/}
            {/*                    if (onClickDeleteAsset) onClickDeleteAsset(asset);*/}
            {/*                },*/}
            {/*                enable: !isCms,*/}
            {/*            },*/}
            {/*        ]}*/}
            {/*    />*/}
            {/*    /!* <div className={clsx(`btn-dropdown`, `btn-dropdown-toolbar`, { "dropdown-open": isShowOptions })}>*/}
            {/*        <button*/}
            {/*            type="button"*/}
            {/*            className="btn btn-xs btn-round btn-dropdown-trigger"*/}
            {/*            onMouseEnter={() => {*/}
            {/*                setIsShowOptions(true);*/}
            {/*            }}*/}
            {/*        >*/}
            {/*            <i className="sprite sprite-etc"></i>*/}
            {/*            <span className={"hidden"}>{t(`common::label::Etc`)}</span>*/}
            {/*        </button>*/}
            {/*        <div className="btn-dropdown-layer">*/}
            {/*            <ul className="btn-dropdown-list">*/}
            {/*                {!hiddenCreateJobButton && */}
            {/*                    <li>*/}
            {/*                        <button*/}
            {/*                            type={"button"}*/}
            {/*                            disabled={disabledCreateJob}*/}
            {/*                            onClick={(e) => {*/}
            {/*                                e.stopPropagation();*/}
            {/*                                if (onClickCreateJob) onClickCreateJob(asset.id);*/}
            {/*                            }}*/}
            {/*                        >*/}
            {/*                            {t(`common::label::Create {{name}}`, { name: t(`common::label::Video`) })}*/}
            {/*                        </button>*/}
            {/*                    </li>*/}
            {/*                }*/}
            {/*                */}
            {/*                <li>*/}
            {/*                    <button*/}
            {/*                        type="button"*/}
            {/*                        onClick={(e) => {*/}
            {/*                            e.stopPropagation();*/}
            {/*                            if (onClickDeleteAsset) onClickDeleteAsset(asset);*/}
            {/*                        }}*/}
            {/*                    >*/}
            {/*                        {t(`common::label::Delete`)}*/}
            {/*                    </button>*/}
            {/*                </li>*/}
            {/*            </ul>*/}
            {/*        </div>*/}
            {/*    </div> *!/*/}
            {/*</CardTool>*/}
            {/*<CardFooter>*/}
            {/*    <StatusBadge status={asset.status} />*/}
            {/*    /!* TODO: error message*/}
            {/*        상태를 Active 또는 Inactive로 변경 시도하였을 경우 실패시 상태는 Inactive로 되고 Error Icon이 노출되고 Hover시에 사유가 노출됩니다.*/}
            {/*        https://app.zeplin.io/project/5cfe4263e414901602ab42e8/screen/5ed4c2e04d5ade51e911485d*/}
            {/*    *!/*/}
            {/*</CardFooter>*/}
        </Card>
    );
};
AssetCard.propTypes = {
    asset: PropTypes.object,
    checkable: PropTypes.bool,
    renderTools: PropTypes.func,
    renderBody: PropTypes.func,
    renderFooter: PropTypes.func,
};

export default AssetCard;
